"use client";

import { CSSProperties, FunctionComponent, ReactNode, forwardRef } from "react";
import { Body, Loader, Label, Wrapper, Adornments } from "./option.styles";
import { Spinner } from "../spinner/spinner";
import { KeyValueObject, ButtonLink, TailwindColors } from "@components/common/types/general";
import { Link } from "../link/link";

export interface OptionProps {
    /**
     * Transform the option to a link.
     */
    link?: ButtonLink;

    /**
     * A callback function which will be fired on click.
     */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * Caption of the option.
     */
    label?: ReactNode | string;

    /**
     * Size of the option.
     */
    size?: "sm" | "lg";

    /**
     * Border radius of the button nav item.
     */
    rounded?: "md" | "lg" | "full";

    /**
     * Color of the option.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Will display a loading spinner and disable the option.
     */
    loading?: boolean | false;

    /**
     * Tag of the option.
     */
    tag?: "div" | "button";

    /**
     * Will disable the option.
     */
    disabled?: boolean | false;

    /**
     * Transform the option to a divider.
     */
    divider?: boolean | false;

    /**
     * Border type of the divider.
     */
    dividerType?: "solid" | "dashed" | "dotted";

    /**
     * Left/right inset of the divider.
     */
    dividerInset?: CSSProperties["marginInline"];

    /**
     * Left adornments of the option.
     */
    leftAdornments?: ReactNode | string;

    /**
     * Right adornments of the option.
     */
    rightAdornments?: ReactNode | string;

    /**
     * Force the active style of the option (similar to hover).
     */
    active?: boolean | false;

    /**
     * Render dom attributes, eg. aria-label.
     */
    attributes?: KeyValueObject;

    /**
     * Class name of the option.
     */
    className?: string;
}

export const Option = forwardRef<HTMLDivElement, OptionProps>(
    (
        {
            link,
            onClick,
            label,
            color,
            loading,
            disabled,
            divider,
            dividerType = "solid",
            dividerInset,
            rounded,
            size,
            leftAdornments,
            rightAdornments,
            active,
            tag = "button",
            attributes,
            className,
        },
        ref,
    ) => {
        const CustomTag = divider ? "div" : link ? Link : tag;
        const customProps = divider ? {} : link ? { onClick, ...link } : { onClick };

        return (
            <Wrapper
                as={CustomTag}
                {...customProps}
                {...attributes}
                type={tag === "button" ? "button" : undefined}
                disabled={loading || disabled ? true : undefined}
                color={color}
                active={active}
                rounded={rounded}
                divider={divider}
                dividerType={dividerType}
                dividerInset={dividerInset}
                className={`group ${active ? "active" : ""} ${className || ""}`.trim()}
                ref={ref}
            >
                {!divider && (
                    <OptionBody
                        color={color}
                        label={label}
                        size={size}
                        loading={loading}
                        leftAdornments={leftAdornments}
                        rightAdornments={rightAdornments}
                    />
                )}
            </Wrapper>
        );
    },
);

interface OptionBodyProps {
    label?: OptionProps["label"];
    loading?: OptionProps["loading"];
    size?: OptionProps["size"];
    color?: OptionProps["color"];
    leftAdornments?: OptionProps["leftAdornments"];
    rightAdornments?: OptionProps["rightAdornments"];
}

const OptionBody: FunctionComponent<OptionBodyProps> = ({ color, label, loading, size, leftAdornments, rightAdornments }) => {
    return (
        <Body size={size}>
            {leftAdornments && <Adornments color={color}>{leftAdornments}</Adornments>}
            {label && <Label>{label}</Label>}
            {loading && (
                <Loader>
                    <Spinner />
                </Loader>
            )}
            {rightAdornments && <Adornments color={color}>{rightAdornments}</Adornments>}
        </Body>
    );
};

Option.displayName = "Option";
